import { useEffect, useState } from "react";
import { Loader2, Trash2, Send, CheckCircle2, MessageCircleQuestion, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtDate } from "@/lib/api";

function Request({ r, reload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); } };
  const reply = (e) => { e.preventDefault(); run(() => api.post(`/admin/support-requests/${r.id}/reply`, { text }), r.user_id ? "Antwort als Mitteilung gesendet" : "Antwort per E-Mail gesendet"); };
  const del = () => window.confirm("Anfrage löschen?") && run(() => api.delete(`/admin/support-requests/${r.id}`), "Gelöscht");
  return (
    <li className="rounded-2xl border border-pink-100 p-4 space-y-3" data-testid={`support-req-${r.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">{r.name} {!r.user_id && <span className="text-xs font-semibold text-slate-400">· Besucher · {r.email}</span>}</p>
          <p className="text-xs text-slate-400">{fmtDate(r.created_at)}</p>
        </div>
        {r.status === "answered" ? <span className="text-xs font-bold text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> beantwortet</span> : <span className="text-xs font-bold text-rose-600">offen</span>}
        <button onClick={del} disabled={busy} data-testid={`support-req-delete-${r.id}`} aria-label="Löschen" className="p-1.5 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
      </div>
      <p className="text-sm bg-rose-50 rounded-xl p-3 whitespace-pre-wrap">{r.question}</p>
      {r.transcript?.length > 0 && (
        <details className="text-xs text-slate-500"><summary className="cursor-pointer font-semibold">Verlauf mit Itzi ({r.transcript.length})</summary>
          <ul className="mt-2 space-y-1">{r.transcript.map((m, i) => <li key={i} className="whitespace-pre-wrap"><b>{m.role === "user" ? "Nutzer" : "Itzi"}:</b> {m.text}</li>)}</ul>
        </details>
      )}
      {r.reply ? <p className="text-sm text-emerald-800 bg-emerald-50 rounded-xl p-3 whitespace-pre-wrap" data-testid={`support-req-reply-${r.id}`}><b>Eure Antwort:</b> {r.reply}</p> : (
        <form onSubmit={reply} className="flex gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} minLength={2} maxLength={2000} required placeholder="Antwort schreiben …" data-testid={`support-req-input-${r.id}`} className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-pink-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rose-300" />
          <button disabled={busy || text.trim().length < 2} data-testid={`support-req-send-${r.id}`} className="self-end h-10 px-4 rounded-full pink-gradient text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"><Send className="w-4 h-4" /> Senden</button>
        </form>
      )}
    </li>
  );
}

function Topics() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/assistant/topics").then(({ data }) => setD(data)).catch(() => setD({ total: 0, topics: [] })); }, []);
  const build = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/assistant/topics"); setD(data); toast.success("Auswertung aktualisiert"); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const max = Math.max(1, ...(d?.topics || []).map((t) => t.count));
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4" data-testid="admin-assistant-topics">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><Sparkles className="w-5 h-5 text-rose-500" /> Häufige Fragen an Itzi</h3>
          <p className="text-sm text-slate-500">Die KI fasst die Fragen der letzten 30 Tage zu Themen zusammen – so seht ihr, wo Mitglieder hängen bleiben.</p>
          {d && <p className="text-xs text-slate-400 mt-1" data-testid="assistant-topics-meta">{d.total} Fragen in 30 Tagen{d.generated_at ? ` · zuletzt ausgewertet ${fmtDate(d.generated_at)} (${d.analysed} Fragen)` : ""}</p>}
        </div>
        <button onClick={build} disabled={busy} data-testid="assistant-topics-build" className="h-10 px-4 rounded-full pink-gradient text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {d?.topics?.length ? "Neu auswerten" : "Jetzt auswerten"}
        </button>
      </div>
      {d?.topics?.length > 0 ? (
        <ol className="space-y-3">
          {d.topics.map((t, i) => (
            <li key={i} className="rounded-2xl border border-pink-100 p-4 space-y-2 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`assistant-topic-${i}`}>
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-rose-100 text-rose-600 text-xs font-extrabold flex items-center justify-center">{i + 1}</span>
                <p className="flex-1 font-bold text-sm">{t.topic}</p>
                <span className="text-xs font-bold text-slate-500">{t.count}×</span>
              </div>
              <div className="h-2 rounded-full bg-rose-50 overflow-hidden"><div className="h-full pink-gradient rounded-full" style={{ width: `${(t.count / max) * 100}%` }} /></div>
              {t.examples?.length > 0 && <ul className="text-xs text-slate-500 space-y-0.5">{t.examples.map((e, j) => <li key={j}>„{e}“</li>)}</ul>}
              {t.hint && <p className="text-xs text-amber-800 bg-amber-50 rounded-xl px-3 py-2 flex gap-1.5"><Lightbulb className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {t.hint}</p>}
            </li>
          ))}
        </ol>
      ) : d && <p className="text-sm text-slate-400" data-testid="assistant-topics-empty">Noch keine Auswertung – tippt auf „Jetzt auswerten“, sobald einige Fragen da sind.</p>}
    </div>
  );
}

function Requests() {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/support-requests").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4" data-testid="admin-support-requests">
      <div>
        <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><MessageCircleQuestion className="w-5 h-5 text-rose-500" /> Support-Anfragen</h3>
        <p className="text-sm text-slate-500">Fragen, die der KI-Assistent Itzi an euch weitergeleitet hat. Mitglieder bekommen eure Antwort als Mitteilung, Besucher per E-Mail.</p>
      </div>
      {list === null ? <Loader2 className="w-5 h-5 animate-spin text-rose-400" /> : list.length === 0 ? <p className="text-sm text-slate-400" data-testid="support-req-empty">Noch keine Anfragen – Itzi hat bisher alles selbst beantwortet 💖</p>
        : <ul className="space-y-3">{list.map((r) => <Request key={r.id} r={r} reload={load} />)}</ul>}
    </div>
  );
}

export function SupportRequestsAdmin() {
  return <div className="space-y-6"><Topics /><Requests /></div>;
}
